// HUELLA / CARA DEL DISPOSITIVO (WebAuthn) — el lado del navegador.
//
// Aquí solo hay traducción de formatos y las dos ceremonias. Todo lo que decide si se entra o no lo
// comprueba el servidor (backend/services/webauthn.js): el reto, el origen, la firma y de quién es
// la credencial. Este fichero no puede "dar por bueno" nada.
//
// LA HUELLA NO SALE DEL APARATO. Se queda en su chip seguro; lo único que viaja es una firma que solo
// ese dispositivo sabe hacer. Ni la aplicación ni la clínica pueden leerla, guardarla ni recuperarla.
import api from './api'
// t169b (12-oct-2026) · una hoja de la huella o la cara a la vez (utils/faceIdUnaVez): un segundo toque o una segunda pantalla
// que la pida mientras hay una abierta se quedan en nada ({ enCurso: true }).
import { conLaHoja } from '../utils/faceIdUnaVez'
import { t as i18nT } from '../utils/i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// WebAuthn habla en ArrayBuffer y el JSON en texto: base64url en los dos sentidos.
const aB64url = (buf) => {
  const b = new Uint8Array(buf)
  let s = ''
  for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i])
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
const deB64url = (s) => {
  const t = String(s || '').replace(/-/g, '+').replace(/_/g, '/')
  const bin = atob(t + '='.repeat((4 - t.length % 4) % 4))
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

// ¿Este aparato tiene lector de huella/cara (o PIN de Windows Hello) que el navegador pueda usar?
//
// DENTRO DE LA APP DEL MÓVIL, NO (5-oct-2026). El dueño: «no me permiten registrar mi huella». La app es la web dentro de
// la vista del sistema (Capacitor), y ahí la huella del NAVEGADOR (WebAuthn) no se puede usar: el iPhone solo la deja a
// las apps asociadas a biango.app (y la publicada todavía no lo está) y la vista web de Android no la trae. El navegador
// del iPhone decía que sí había lector, se ofrecía «Activar», y al pulsar el sistema lo rechazaba. En la app la huella y la
// cara van por la LLAVE DEL MÓVIL (services/entradaBiometrica, Face ID o la huella del propio sistema), que necesita la
// versión nueva de la app. Aquí se contesta «no» y quien decide qué ofrecer es services/huellaOCara.
export async function hayLector() {
  try {
    const cap = typeof window !== 'undefined' ? window.Capacitor : null
    if (cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform()) return false
    return !!(window.PublicKeyCredential && await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable())
  } catch (_) { return false }
}

// LO QUE DICE EL NAVEGADOR CUANDO NO DEJA REGISTRAR O USAR LA HUELLA, EN CRISTIANO (5-oct-2026). Antes salía su texto en
// inglés («The operation either timed out or was not allowed…») y parecía que la app estaba rota.
export function explicarFalloHuella(e) {
  const n = e && e.name
  if (e && e.response && e.response.data && e.response.data.error) return e.response.data.error
  if (n === 'NotAllowedError') return i18nT('services.huella.el_dispositivo_no_ha_dejado', 'El dispositivo no ha dejado usar la huella o la cara (se ha cancelado o ha pasado el tiempo). Vuelve a intentarlo y, cuando lo pida, pon el dedo o la cara.')
  if (n === 'InvalidStateError') return i18nT('services.huella.este_dispositivo_ya_tenia_la', 'Este dispositivo ya tenía la huella o la cara activada para tu cuenta.')
  if (n === 'SecurityError') return i18nT('services.huella.este_navegador_no_deja_usar', 'Este navegador no deja usar la huella en esta dirección. Entra por https://biango.app y vuelve a intentarlo.')
  if (n === 'NotSupportedError') return i18nT('services.huella.este_navegador_no_sabe_usar', 'Este navegador no sabe usar la huella ni la cara. Prueba con Chrome, Safari o Edge actualizados.')
  if (n === 'AbortError') return i18nT('services.huella.se_ha_cancelado_vuelve_a', 'Se ha cancelado. Vuelve a intentarlo.')
  return (e && e.message) || i18nT('services.huella.no_se_ha_podido_usar', 'No se ha podido usar la huella o la cara.')
}

// Nombre por defecto del dispositivo. Se propone uno reconocible porque una lista de credenciales
// llamadas todas "Este dispositivo" es una lista que nadie se atreve a tocar el día que hay que
// quitar el móvil que se ha perdido.
export function nombreDispositivo() {
  const ua = navigator.userAgent || ''
  if (/iPhone/i.test(ua)) return 'iPhone'
  if (/iPad/i.test(ua)) return 'iPad'
  if (/Android/i.test(ua)) return 'Móvil Android'
  if (/Macintosh/i.test(ua)) return 'Mac'
  if (/Windows/i.test(ua)) return 'Ordenador Windows'
  return 'Este dispositivo'
}

// ── ¿HAY UNA HUELLA DE ESTE APARATO? ───────────────────────────────────────────────────────────
//
// EL SERVIDOR NO LO SABE, Y NO ES UN DESCUIDO. Él sabe cuántas credenciales tiene la cuenta (la del
// móvil y la del ordenador de recepción son dos filas iguales), pero no puede saber en cuál de los
// dos aparatos estás ahora mismo: eso no viaja en ninguna petición. Y la diferencia importa justo
// para lo que hace falta aquí — pedir la huella SOLA al abrir. Pedirla en un ordenador donde esta
// persona nunca la registró abre un diálogo que no puede acabar bien (o un código QR para ir a
// buscar el móvil), y quien está delante se queda esperando algo que no ha pedido.
//
// QUIEN SÍ LO SABE ES ESTE NAVEGADOR, porque la credencial se creó aquí. Se apunta al registrarla, y
// también la primera vez que se usa con éxito en este aparato — así lo aprenden los que ya la tenían
// antes de que esto existiera, sin obligar a nadie a volver a registrarse.
//
// NO ES SESIÓN, y por eso NO va en `CLAVES_SESION` (services/sesion): la llave sigue estando en el
// chip de este aparato cuando la persona cierra sesión y vuelve a entrar. Lo que se guarda es el
// identificador PÚBLICO de la credencial (el mismo que el servidor manda en `allowCredentials`), por
// cuenta: en el ordenador de recepción entran varias personas y cada una tiene el suyo.
const CLAVE_APARATO = 'huella_de_este_aparato'

const mapaAparato = () => {
  try { const m = JSON.parse(localStorage.getItem(CLAVE_APARATO) || '{}'); return (m && typeof m === 'object') ? m : {} }
  catch (_) { return {} }
}
const guardarMapa = (m) => { try { localStorage.setItem(CLAVE_APARATO, JSON.stringify(m)) } catch (_) {} }
// De quién es la sesión, cuando quien llama no lo dice. Es el usuario REAL (el que `login()` guarda),
// que es el mismo con el que el servidor busca las credenciales.
const idDeLaSesion = () => {
  try { const u = JSON.parse(localStorage.getItem('user') || 'null'); return u && u.id != null ? String(u.id) : null }
  catch (_) { return null }
}
const clave = (agenteId) => (agenteId != null ? String(agenteId) : idDeLaSesion())

/** El identificador de la credencial que ESTA persona registró en ESTE aparato, o null. */
export function huellaDeEsteAparato(agenteId) {
  const k = clave(agenteId)
  if (!k) return null
  const v = mapaAparato()[k]
  return typeof v === 'string' && v ? v : null
}
export function recordarHuellaDeEsteAparato(credId, agenteId) {
  const k = clave(agenteId)
  if (!k || !credId) return
  const m = mapaAparato()
  if (m[k] === credId) return
  m[k] = String(credId)
  guardarMapa(m)
}
export function olvidarHuellaDeEsteAparato(agenteId) {
  const k = clave(agenteId)
  if (!k) return
  const m = mapaAparato()
  if (!(k in m)) return
  delete m[k]
  guardarMapa(m)
}

// ── REGISTRAR ESTE DISPOSITIVO ─────────────────────────────────────────────────────────────────
export async function registrarHuella(nombre, agenteId) {
  const { data: o } = await api.post('/seguridad/huella/registro/inicio')
  const cred = await navigator.credentials.create({
    publicKey: {
      challenge: deB64url(o.challenge),
      rp: o.rp,
      user: { ...o.user, id: deB64url(o.user.id) },
      pubKeyCredParams: o.pubKeyCredParams,
      authenticatorSelection: o.authenticatorSelection,
      excludeCredentials: (o.excludeCredentials || []).map(c => ({ ...c, id: deB64url(c.id) })),
      timeout: o.timeout, attestation: o.attestation,
    },
  })
  if (!cred) throw new Error(i18nT('services.huella.no_se_ha_registrado_nada', 'No se ha registrado nada'))
  // LA CLAVE PÚBLICA, DIRECTA. `getPublicKey()` la entrega ya en el formato que Node entiende, así
  // que no hace falta decodificar CBOR en el servidor — que es justo donde se cometen los fallos en
  // esto. A cambio, exige un navegador razonablemente moderno; si no lo es, se dice claramente en
  // vez de guardar algo a medias.
  const r = cred.response
  const pk = r.getPublicKey ? r.getPublicKey() : null
  if (!pk) throw new Error(i18nT('services.huella.tu_navegador_es_demasiado_antiguo', 'Tu navegador es demasiado antiguo para registrar la huella. Actualízalo (Chrome 85+, Safari 16+, Firefox 119+).'))
  const { data } = await api.post('/seguridad/huella/registro/fin', {
    id: cred.id,
    clientDataJSON: aB64url(r.clientDataJSON),
    publicKey: aB64url(pk),
    algoritmo: r.getPublicKeyAlgorithm ? r.getPublicKeyAlgorithm() : -7,
    transports: r.getTransports ? r.getTransports() : null,
    nombre: nombre || nombreDispositivo(),
  })
  // SE APUNTA QUE ESTA CREDENCIAL ES DE ESTE APARATO. Es lo que permite pedir la huella sola al
  // abrir sin adivinar nada: solo se pide donde consta que se creó.
  recordarHuellaDeEsteAparato(cred.id, agenteId)
  return data
}

// ── DESBLOQUEAR ────────────────────────────────────────────────────────────────────────────────
// Devuelve { ok } o lanza. `sinCredenciales` no es un error: es "este usuario todavía no ha
// registrado ninguna huella", y quien llama debe ofrecer registrarla en vez de enseñar un fallo.
//
// `soloDeEsteAparato` es para PEDIRLA SOLA, sin que nadie pulse nada (la pantalla de bloqueo, al
// abrir). Cambia dos cosas, y las dos importan:
//   · Antes de abrir NINGÚN diálogo se comprueba que la credencial apuntada en este aparato siga
//     estando en la lista del servidor. Si no está —se quitó el dispositivo desde Mi perfil— se
//     olvida aquí mismo y se contesta `noEsDeEsteAparato`: nadie se queda mirando un diálogo que no
//     puede acabar bien. La lista la manda el servidor, no se adivina.
//   · Y se pide EXACTAMENTE esa credencial, no las de todos sus aparatos: con las otras dentro, el
//     navegador ofrece el código QR para ir a buscar el móvil, que es lo último que quiere ver quien
//     solo ha abierto la aplicación.
// Sin la opción, el botón se comporta como siempre: se ofrecen todas y vale cualquiera.
export function desbloquearConHuella(opciones = {}) { return conLaHoja(() => desbloquearConHuellaYa(opciones)) }

// ── t169c (12-oct-2026) · EL RETO, PEDIDO ANTES DEL TOQUE ─────────────────────────────────────────────────────────
//  Safari (el del iPhone sobre todo) solo abre la hoja de Face ID si `navigator.credentials.get()` se llama DENTRO del toque,
//  sin esperar antes a nada: con la petición del reto por medio, el toque «se gasta» y Safari dice que no (o lo deja para
//  otro toque). La pantalla tapada pide el reto en cuanto se tapa (`prepararHuella`) y al tocar se usa ese, sin esperar.
//  Vale RETO_LISTO_MS (el servidor lo guarda 5 minutos); uno usado o caducado no se vuelve a usar.
const RETO_LISTO_MS = 4 * 60 * 1000
let retoListo = null
export async function prepararHuella(agenteId) {
  // Solo donde esta persona registró la huella (en otro aparato, el toque la pide como siempre: con el código QR).
  if (!huellaDeEsteAparato(agenteId)) { retoListo = null; return false }
  try {
    const { data } = await api.post('/seguridad/huella/reto')
    retoListo = { quien: String(agenteId == null ? '' : agenteId), o: data, at: Date.now() }
    return true
  } catch (_) { retoListo = null; return false }
}
function tomarRetoListo(agenteId) {
  const r = retoListo
  retoListo = null
  return r && r.quien === String(agenteId == null ? '' : agenteId) && Date.now() - r.at < RETO_LISTO_MS ? r.o : null
}
async function desbloquearConHuellaYa(opciones = {}) {
  const { soloDeEsteAparato = false, agenteId = null } = opciones
  const o = tomarRetoListo(agenteId) || (await api.post('/seguridad/huella/reto')).data
  if (o.sinCredenciales) { olvidarHuellaDeEsteAparato(agenteId); return { sinCredenciales: true } }
  const permitidas = (o.allowCredentials || [])
  const mia = huellaDeEsteAparato(agenteId)
  const aquiSigue = mia && permitidas.some(c => String(c.id) === mia)
  if (soloDeEsteAparato && !aquiSigue) {
    if (mia) olvidarHuellaDeEsteAparato(agenteId)
    return { noEsDeEsteAparato: true }
  }
  const lista = soloDeEsteAparato ? permitidas.filter(c => String(c.id) === mia) : permitidas
  const cred = await navigator.credentials.get({
    publicKey: {
      challenge: deB64url(o.challenge),
      rpId: o.rpId,
      allowCredentials: lista.map(c => ({ ...c, id: deB64url(c.id) })),
      userVerification: o.userVerification,
      timeout: o.timeout,
    },
  })
  if (!cred) throw new Error(i18nT('services.huella.no_se_ha_podido_verificar', 'No se ha podido verificar'))
  const r = cred.response
  const { data } = await api.post('/seguridad/huella/verificar', {
    id: cred.id,
    clientDataJSON: aB64url(r.clientDataJSON),
    authenticatorData: aB64url(r.authenticatorData),
    signature: aB64url(r.signature),
  })
  // ASÍ LO APRENDEN LOS APARATOS DE ANTES. Quien ya tenía la huella registrada cuando esto se
  // escribió no tiene nada apuntado: la primera vez que la usa aquí y el SERVIDOR la da por buena,
  // este aparato queda marcado y a partir de entonces se le pide sola. `cross-platform` es la única
  // que se descarta: esa firma viene de otro aparato (el móvil por código QR), no de este.
  if (data && data.ok && cred.authenticatorAttachment !== 'cross-platform') {
    recordarHuellaDeEsteAparato(cred.id, agenteId)
  }
  return data
}

// ── ENTRAR SIN SESIÓN CON LA HUELLA DE ESTE NAVEGADOR (5-oct-2026) ─────────────────────────────────
// Cuando la sesión ha caducado (o se ha salido), la pantalla de entrar ofrece la huella si ESTE navegador registró alguna:
// los identificadores PÚBLICOS que se apuntaron al registrarla (arriba, `huella_de_este_aparato`). El servidor da el reto,
// el dispositivo firma con la huella o la cara, y el servidor da la misma sesión que la contraseña
// (routes/entradaBiometrica.js, /web/reto y /web/entrar). Devuelve { token, agente }, o { olvidar } si el servidor dice
// que ya no vale (se borra de aquí), o lanza si el navegador no deja.
export function credencialesDeEsteNavegador() {
  return [...new Set(Object.values(mapaAparato()).filter((v) => typeof v === 'string' && v))].slice(0, 5)
}
export function entrarConHuella() { return conLaHoja(entrarConHuellaYa) }
// t169c · lo mismo para ENTRAR (la pantalla de entrar del navegador): el reto se pide al pintarse el botón, y al tocarlo la
// hoja sale en el acto, dentro del toque (Safari). Si el servidor dice que la huella ya no vale, se olvida aquí.
let entradaLista = null
export async function prepararEntradaConHuella() {
  const ids = credencialesDeEsteNavegador()
  if (!ids.length) { entradaLista = null; return false }
  try {
    const { data } = await api.post('/auth/biometria/web/reto', { credenciales: ids })
    entradaLista = { ids: ids.join(','), o: data, at: Date.now() }
    return true
  } catch (e) {
    entradaLista = null
    if (e?.response?.data?.olvidar) guardarMapa({})
    return false
  }
}
function tomarEntradaLista(ids) {
  const r = entradaLista
  entradaLista = null
  return r && r.ids === ids.join(',') && Date.now() - r.at < RETO_LISTO_MS ? r.o : null
}
async function entrarConHuellaYa() {
  const ids = credencialesDeEsteNavegador()
  if (!ids.length) return { sinHuella: true }
  let o = tomarEntradaLista(ids)
  if (!o) {
    try { o = (await api.post('/auth/biometria/web/reto', { credenciales: ids })).data }
    catch (e) {
      if (e?.response?.data?.olvidar) { guardarMapa({}); return { olvidar: true, error: e.response.data.error } }
      throw e
    }
  }
  const cred = await navigator.credentials.get({
    publicKey: {
      challenge: deB64url(o.challenge),
      rpId: o.rpId,
      allowCredentials: (o.allowCredentials || []).map(c => ({ ...c, id: deB64url(c.id) })),
      userVerification: o.userVerification,
      timeout: o.timeout,
    },
  })
  if (!cred) throw new Error(i18nT('services.huella.no_se_ha_podido_verificar', 'No se ha podido verificar'))
  const r = cred.response
  try {
    const { data } = await api.post('/auth/biometria/web/entrar', {
      id: cred.id,
      clientDataJSON: aB64url(r.clientDataJSON),
      authenticatorData: aB64url(r.authenticatorData),
      signature: aB64url(r.signature),
    })
    return data
  } catch (e) {
    if (e?.response?.data?.olvidar) {
      const m = mapaAparato()
      for (const k of Object.keys(m)) if (m[k] === cred.id) delete m[k]
      guardarMapa(m)
      return { olvidar: true, error: e.response.data.error }
    }
    throw e
  }
}

export async function estadoHuella() {
  try { return (await api.get('/seguridad/huella/estado')).data }
  catch (_) { return { disponible: false, credenciales: 0, lista: [] } }
}
